import { useMutation } from '@apollo/client';
import { useFetchStudioBooking } from './useFetchStudioBooking';
import { archiveStudioSecure as ARCHIVE_STUDIO_SECURE } from '../graphql/mutations/userStudioMutations';
import { trackStudioArchived } from '../util/tracking';

const STUDIO_DELETE_ERROR_MESSAGE = 'Studio konnte nicht gelöscht werden. Bitte versuche es erneut.';

export const useArchiveStudio = (studioId = null) => {
  const [archiveStudioMutation, { loading, error }] = useMutation(ARCHIVE_STUDIO_SECURE, {
    update(cache, { data }) {
      const archivedStudioId = data?.archiveStudioSecure?.studioId;
      if (!archivedStudioId) return;

      cache.modify({
        fields: {
          studiosByUserID(existingData = { items: [] }) {
            if (existingData?.items) {
              return {
                ...existingData,
                items: existingData.items.filter(
                  (item) => item && item.id !== archivedStudioId
                ),
              };
            }
            if (Array.isArray(existingData)) {
              return existingData.filter((item) => item && item.id !== archivedStudioId);
            }
            return existingData;
          },
          listStudios(existingData = { items: [] }) {
            if (existingData?.items) {
              return {
                ...existingData,
                items: existingData.items.filter(
                  (item) => item && item.id !== archivedStudioId
                ),
              };
            }
            if (Array.isArray(existingData)) {
              return existingData.filter((item) => item && item.id !== archivedStudioId);
            }
            return existingData;
          },
        },
      });
    },
    refetchQueries: ['GetStudiosByUserID', 'ListStudios', 'StudiosForServiceSelection'],
  });

  const { bookingStudioData, loading: fetchingStudio, error: fetchError } = useFetchStudioBooking(
    studioId,
    { includeBookings: true, includeAvailabilitySlots: false }
  );

  const checkForFutureBookings = (checkStudioId = null) => {
    const targetStudioId = checkStudioId || studioId;

    if (!targetStudioId) {
      return { success: false, error: 'Studio konnte nicht zugeordnet werden.' };
    }

    if (fetchingStudio) {
      return { success: false, error: 'Buchungsdaten werden noch geladen.', isLoading: true };
    }

    if (fetchError) {
      console.error('[archiveStudio] booking check failed', fetchError);
      return { success: false, error: 'Buchungsdaten konnten nicht geprüft werden.' };
    }

    if (!bookingStudioData) {
      return { success: false, error: 'Studiodaten konnten nicht geladen werden.' };
    }

    const now = new Date().toISOString();
    const allBookings = bookingStudioData?.bookings?.items || [];

    const futureBookings = allBookings.filter((booking) => {
      if (!booking) return false;
      const isActive = booking.status === 'PENDING' || booking.status === 'CONFIRMED';
      const isFuture = new Date(booking.startDateTime) > new Date(now);
      return isActive && isFuture;
    });

    return {
      success: true,
      hasFutureBookings: futureBookings.length > 0,
      bookingCount: futureBookings.length,
      futureBookings,
    };
  };

  const handleArchiveStudio = async (targetStudioId) => {
    if (!targetStudioId) {
      return { success: false, error: 'Studio konnte nicht zugeordnet werden.' };
    }

    try {
      const { data } = await archiveStudioMutation({
        variables: {
          input: { studioId: targetStudioId },
        },
      });

      if (data?.archiveStudioSecure?.success) {
        trackStudioArchived({
          studioId: data.archiveStudioSecure.studioId || targetStudioId,
          source: 'studio_editor'
        });
        return { success: true, data: data.archiveStudioSecure };
      }

      console.error('[archiveStudio] backend rejected archive request', data?.archiveStudioSecure);
      throw new Error(STUDIO_DELETE_ERROR_MESSAGE);
    } catch (err) {
      console.error('[archiveStudio] backend/Amplify error', {
        message: err?.message,
        graphQLErrors: err?.graphQLErrors,
        networkError: err?.networkError,
      });
      return {
        success: false,
        error: STUDIO_DELETE_ERROR_MESSAGE,
      };
    }
  };

  return {
    archiveStudio: handleArchiveStudio,
    checkForFutureBookings,
    loading,
    fetchingStudio,
    error,
    fetchError,
  };
};
